






































































































import Vue from 'vue';
import { timeFormat, count } from 'd3';
import { ICoupon, IDecoratedSession, ISession } from '@/store/types';
// @ts-ignore
import { ListLoader } from 'vue-content-loader';
import * as actions from '@/store/action-types';
import * as mutations from '@/store/mutation-types';
import DatePicker from '@/components/UI/DatePicker.vue';
import SessionList from '@/components/CancelAnalytics/LiteSessionList.vue';
import VueVegaEmbed from '@/components/Vis/Vega/VueVegaEmbed.vue';
import { genBarTimelineSpec } from '@/components/Vis/Vega/Specs/BarTimelineSpec';
import { genCancellationTimelineSpec } from '@/components/Vis/Vega/Specs/CancellationTimelineSpec';
import { genHeatmapSpec } from '@/components/Vis/Vega/Specs/HeatmapSpec';
import { TopLevelSpec } from 'vega-lite';

const dateTimeFormat = timeFormat('%b %_d');

export default Vue.extend({
  name: 'LiteDashboard',
  components: {
    DatePicker,
    SessionList,
    ListLoader,
    VueVegaEmbed,
  },
  data(): { loading: boolean; circularCancellationHeatmap: boolean; offerStack: boolean; customerFilterSelection: string } {
    return {
      loading: true,
      circularCancellationHeatmap: false,
      offerStack: true,
      customerFilterSelection: '',
    };
  },
  computed: {
    dateRange(): [Date, Date] {
      return this.$store.state.dashboardDateRange;
    },
    formattedDateSelection(): string {
      return `from ${dateTimeFormat(this.dateRange[0])} to ${dateTimeFormat(this.dateRange[1])}`;
    },
    // for knowing when to refresh visuals
    selectionKey(): string {
      return this.dateRange[0].toString() + this.dateRange[1].toString() + this.sessions.length;
    },
    sessions(): ISession[] {
      return this.$store.state.sessions;
    },
    selectedSessions(): IDecoratedSession[] {
      return this.$store.getters.selectedSessions;
    },
    visDailySessions(): TopLevelSpec | null {
      if (this.selectedSessions && this.selectedSessions.length) {
        return genBarTimelineSpec({
          data: this.selectedSessions,
          xField: 'createdAt',
          timeUnit: 'yearmonthdate',
        });
      }
      return null;
    },
    sessionCount(): number {
      return (this.selectedSessions || []).length;
    },
    completedSessionCount(): number {
      return (this.selectedSessions || []).reduce((tally: number, session: ISession) => tally + (session.aborted ? 0 : 1), 0);
    },
    cancelCount(): number {
      return (this.selectedSessions || []).reduce((tally: number, session: ISession) => tally + (session.canceled ? 1 : 0), 0);
    },
    sessionCustomers(): number {
      return [...new Set(this.selectedSessions.map((s) => s.customer?.email))].length;
    },
    visDailyCancels(): TopLevelSpec | null {
      if (this.selectedSessions && this.selectedSessions.length) {
        return genBarTimelineSpec({
          data: this.selectedSessions.filter((d) => d.canceled),
          xField: 'createdAt',
          timeUnit: 'yearmonthdate',
        });
      }
      return null;
    },
    visCancellationTimeline(): TopLevelSpec | null {
      if (this.selectedSessions && this.selectedSessions.length) {
        return genCancellationTimelineSpec({
          data: this.selectedSessions.filter((d) => d.canceled),
          xField: 'createdAt',
          timeUnit: 'week',
          yField: 'surveyChoiceValue',
          xLabels: true,
          yAxisTitle: 'Cancellations',
          yLabels: true,
        });
      }
      return null;
    },
    cancellationReasonHeatmap(): TopLevelSpec | null {
      if (this.loading) {
        return null;
      }
      if (this.selectedSessions && this.selectedSessions.length) {
        const totalResponses = this.selectedSessions.filter((d) => d.surveyResponse).length;
        const responseFrequencies = this.selectedSessions.reduce(
          (counts, session) => {
            if (session.surveyResponse) {
              counts[session.surveyResponse] = counts[session.surveyResponse] ? counts[session.surveyResponse] + 1 : 1;
            }
            return counts;
          },
          {} as { [key: string]: number }
        );
        const responsePct = Object.keys(responseFrequencies).reduce(
          (pcts, response) => {
            pcts[response] = `${((responseFrequencies[response] / totalResponses) * 100).toFixed(0)}%`;
            return pcts;
          },
          {} as { [key: string]: string }
        );
        const heatmapData = this.selectedSessions
          .filter((s) => s.surveyResponse)
          // eslint-disable-next-line arrow-body-style
          .map((session) => {
            return {
              ...session,
              Date: session.createdAt,
              // @ts-ignore
              'Reason for Cancellation': `${session.surveyResponse} (${responsePct[session.surveyResponse]})`,
            };
          });
        return genHeatmapSpec({
          data: heatmapData,
          xField: 'Date',
          yField: 'Reason for Cancellation',
          timeUnit: 'utcmonthdate',
          xLabels: true,
          yLabels: true,
          // yAxisTitle: 'Response',
          circular: this.circularCancellationHeatmap,
          height: 240,
        });
      }
      return null;
    },
  },
  async created() {
    await this.$store.dispatch(actions.GET_SESSIONS_AND_BLUEPRINTS, { startDate: this.dateRange[0], endDate: this.dateRange[1] });
    this.loading = false;
  },
  watch: {
    dateRange(newValue, oldValue): void {
      if (newValue[0] !== oldValue[0] || newValue[1] !== oldValue[1]) {
        this.fetchSessions();
      }
    },
  },
  methods: {
    handleDateChange(value: Date[]): void {
      this.$store.commit(mutations.SET_DASHBOARD_DATE_RANGE, value);
    },
    async fetchSessions(): Promise<void> {
      this.loading = true;
      await this.$store.dispatch(actions.GET_SESSIONS_AND_BLUEPRINTS, { startDate: this.dateRange[0], endDate: this.dateRange[1] });
      this.loading = false;
    },
  },
});
